import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Card, CardBody, Col, Row, Spinner } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import { fetchDriverAnalysisSummary } from '../actions/action';

/* Chart chrome. Text wears ink tokens, never a series colour - a coloured mark
   beside a label is what carries identity. */
const INK = {
  surface: '#fcfcfb',
  primary: '#0b0b0b',
  secondary: '#52514e',
  muted: '#898781',
  grid: '#e1e0d9',
};

/* Delivery outcome MEANS good / degraded / bad, so it wears the reserved status
   scale rather than categorical hues, and always ships with a legend label. */
const STATUS = {
  success: '#0ca30c',
  partial: '#ec835a',
  failed: '#d03b3b',
};

/* Driver names are nominal - one series, one hue for every bar. Colouring them
   by value would re-encode what the bar length already shows. */
const SERIES_BLUE = '#2a78d6';

/* Categorical slots, fixed order, assigned in sequence and never cycled - the
   order is what keeps them apart under colourblindness. Validated for 6 slots:
   worst adjacent pair ΔE 9.1 under protanopia, 19.6 normal vision. Three sit
   below 3:1 on the surface, so slices ship with visible labels and a legend. */
const CATEGORICAL = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300'];
const OTHER_GREY = '#898781';

/* Label ink per slice - whichever of near-black / white clears on that fill.
   Apexcharts would otherwise print white on the yellow slice at 2.17:1. */
const SLICE_LABEL_INK = ['#0b0b0b', '#0b0b0b', '#0b0b0b', '#0b0b0b', '#0b0b0b', '#ffffff', '#0b0b0b'];

/* A pie is only honest at a glance with a handful of slices, so the tail folds
   into one "Other" rather than generating more hues. */
const MAX_PIE_SLICES = 6;

/* A bar per driver does not scale - at 150 drivers that is a 4,000px plot in a
   300px window. The busiest are charted and the tail is folded into one row,
   named so the cap is never silent. */
const MAX_DRIVER_BARS = 15;

/* Above / below a baseline is a polarity question, so it takes the documented
   diverging pair - blue and red read as opposite, with a neutral grey baseline
   between them. White labels clear 4.5:1 on both fills. */
const DIVERGING = {
  above: '#1c5cab',
  below: '#c2302f',
};

/* Colour key rendered under the title, since distributed columns cannot carry a
   legend without listing every driver. */
const ColourKey = ({ items }) => (
  <div className="d-flex align-items-center gap-3 mb-2" style={{ fontSize: '11px' }}>
    {items.map((item) => (
      <span key={item.label} className="d-inline-flex align-items-center gap-1">
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 3,
            background: item.color,
            display: 'inline-block',
          }}
        />
        <span style={{ color: INK.secondary }}>{item.label}</span>
      </span>
    ))}
  </div>
);

/* 1..5 stars are ordered, so the buckets take a single-hue ordinal ramp,
   light -> dark. Validated: monotone L, adjacent dL >= 0.06, light end 2.06:1
   on the chart surface, hue spread 4 degrees. */
const RATING_RAMP = ['#86b6ef', '#3987e5', '#256abf', '#184f95', '#0d366b'];

const BASE_CHART = {
  toolbar: { show: false },
  fontFamily: 'inherit',
  animations: { enabled: false },
};

const BASE_GRID = {
  borderColor: INK.grid,
  strokeDashArray: 0,
  padding: { top: 0, right: 8, bottom: 0, left: 8 },
};

const AXIS_LABEL = { style: { colors: INK.muted, fontSize: '12px' } };

const driverLabel = (driver) =>
  driver.driverEmpId
    ? `${driver.driverName} (${driver.driverEmpId})`
    : driver.driverName;

/* An average means nothing without volume behind it - with a hundred drivers a
   single five star review would otherwise top the list over someone rated
   thirty times. */
const MIN_RATINGS_FOR_BEST = 3;

/* "Who is the best driver" is one name, not a chart - so it reads as a tile. */
const HighlightTile = ({ label, name, value, hint, accent }) => (
  <Col md={4}>
    <div className="p-2 h-100 border rounded">
      <div
        className="text-uppercase"
        style={{ fontSize: '11px', letterSpacing: '0.4px', color: INK.muted }}
      >
        {label}
      </div>
      <div
        className="text-truncate"
        style={{ fontSize: '16px', fontWeight: 700, color: INK.primary }}
        title={name}
      >
        {name}
      </div>
      <div style={{ fontSize: '13px', fontWeight: 600, color: accent || INK.secondary }}>
        {value}
      </div>
      {hint ? (
        <div style={{ fontSize: '11px', color: INK.muted }}>{hint}</div>
      ) : null}
    </div>
  </Col>
);

const TILE_HEIGHT = 94;

const StatTile = ({ label, value, hint }) => (
  <Col xs={6} md={3}>
    <div
      className="px-3 h-100 border rounded d-flex flex-column justify-content-center"
      style={{ minHeight: TILE_HEIGHT }}
    >
      <div
        className="text-uppercase"
        style={{
          fontSize: '11px',
          letterSpacing: '0.4px',
          color: INK.muted,
          lineHeight: 1.6,
        }}
      >
        {label}
      </div>
      <div
        style={{ fontSize: '22px', fontWeight: 700, color: INK.primary, lineHeight: 1.4 }}
      >
        {value}
      </div>
      {hint ? (
        <div style={{ fontSize: '11px', color: INK.secondary, lineHeight: 1.6 }}>{hint}</div>
      ) : null}
    </div>
  </Col>
);

const ChartCard = ({ title, subtitle, children }) => (
  <Card className="mb-0 h-100">
    <CardBody className="p-2">
      <h5
        className="mb-0"
        style={{ fontSize: '13px', color: INK.primary, lineHeight: 1.6 }}
      >
        {title}
      </h5>
      {subtitle ? (
        <p
          className="mt-1 mb-3"
          style={{ fontSize: '11px', color: INK.muted, lineHeight: 1.7 }}
        >
          {subtitle}
        </p>
      ) : null}
      {children}
    </CardBody>
  </Card>
);

/* Every driver is charted, so a busy period can run to dozens of bars. The plot
   keeps its full height and scrolls inside the card rather than stretching the
   page - bars are sorted busiest first, so the top of the list needs no scroll. */
const CHART_MAX_HEIGHT = 300;

const ScrollablePlot = ({ height, children }) => (
  <div
    style={{
      maxHeight: height > CHART_MAX_HEIGHT ? CHART_MAX_HEIGHT : undefined,
      overflowY: height > CHART_MAX_HEIGHT ? 'auto' : undefined,
      overflowX: 'hidden',
    }}
  >
    {children}
  </div>
);

/**
 * Charts for the Driver Analysis page.
 *
 * Reads the same filters as the listing and aggregates server side, so the
 * charts describe every matching row rather than the ten on the current page.
 */
const DriverAnalysisSummary = ({ params }) => {
  const dispatch = useDispatch();
  const [summary, setSummary] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const paramsKey = JSON.stringify(params || {});

  const loadSummary = useCallback(async (requestParams) => {
    setLoading(true);
    try {
      const response = await fetchDriverAnalysisSummary({
        params: requestParams,
        dispatch,
      });
      setSummary(response || null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => loadSummary(JSON.parse(paramsKey)), 300);
    return () => clearTimeout(delay);
  }, [paramsKey]);

  const totals = summary?.totals;
  const drivers = summary?.drivers || [];
  const distribution = summary?.ratingDistribution || [];
  const hasData = Number(totals?.deliveries) > 0;

  /* Deliveries by driver, split by outcome. Horizontal because driver names are
     long, stacked because the split is part-to-whole within each driver. */
  const chartedDrivers = (() => {
    if (drivers.length <= MAX_DRIVER_BARS) return drivers;
    const head = drivers.slice(0, MAX_DRIVER_BARS);
    const tail = drivers.slice(MAX_DRIVER_BARS);
    const folded = tail.reduce(
      (sum, d) => ({
        driverName: `Other (${tail.length} drivers)`,
        driverEmpId: '',
        deliveries: sum.deliveries + d.deliveries,
        success: sum.success + d.success,
        partial: sum.partial + d.partial,
        failed: sum.failed + d.failed,
        rated: sum.rated + d.rated,
        avgRating: null,
      }),
      { deliveries: 0, success: 0, partial: 0, failed: 0, rated: 0 },
    );
    return [...head, folded];
  })();

  const outcomeOptions = {
    chart: { ...BASE_CHART, type: 'bar', stacked: true },
    colors: [STATUS.success, STATUS.partial, STATUS.failed],
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '62%',
        borderRadius: 4,
        borderRadiusApplication: 'end',
        dataLabels: {
          // The per-bar total sits outside the fill in ink, so the figure is
          // readable without putting pale text on a mid-tone segment.
          total: {
            enabled: true,
            offsetX: 4,
            style: { color: INK.secondary, fontSize: '12px', fontWeight: 600 },
          },
        },
      },
    },
    // 2px of surface between stacked fills so adjacent segments stay separate.
    stroke: { width: 2, colors: [INK.surface] },
    dataLabels: { enabled: false },
    grid: { ...BASE_GRID, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    xaxis: {
      categories: chartedDrivers.map(driverLabel),
      labels: AXIS_LABEL,
      axisBorder: { color: INK.grid },
      axisTicks: { color: INK.grid },
    },
    yaxis: { labels: { ...AXIS_LABEL, maxWidth: 110 } },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      labels: { colors: INK.secondary },
      markers: { radius: 3 },
      itemMargin: { horizontal: 10 },
    },
    tooltip: { shared: true, intersect: false },
  };

  const outcomeSeries = [
    { name: 'Delivered', data: chartedDrivers.map((d) => d.success) },
    { name: 'Partially delivered', data: chartedDrivers.map((d) => d.partial) },
    { name: 'Failed', data: chartedDrivers.map((d) => d.failed) },
  ];

  /* Average rating per driver, best score first - the ranking is the point of
     this chart, so it sorts on the metric rather than on delivery count. Ties
     go to the driver with more ratings behind the score.

     Drawn as a lollipop: a thin stem to the score with a dot at the end. Same
     encoding as a bar (position on a common 0-5 scale) with far less ink, so a
     long list of drivers stays readable. One measure, so no legend - the title
     names it. */
  const ratedDrivers = drivers
    .filter((d) => d.rated > 0)
    .slice()
    .sort((a, b) => b.avgRating - a.avgRating || b.rated - a.rated);

  /* Each score is read against the fleet average, which is a polarity question -
     above or below a baseline - so it takes the documented diverging pair
     (blue <-> red) with a neutral grey baseline between them. */
  const fleetAverage = totals?.avgRating != null ? Number(totals.avgRating) : null;
  /* Horizontal bars so the driver names sit on the axis, readable rather than
     hover-only. A bar per driver does not scale to 150, so this charts the top
     slice by score and names the cap in the subtitle - the full list is in the
     table below. */
  const chartedRatedDrivers = ratedDrivers.slice(0, MAX_DRIVER_BARS);
  const ratingBarColors = chartedRatedDrivers.map((d) =>
    fleetAverage == null || d.avgRating >= fleetAverage ? DIVERGING.above : DIVERGING.below,
  );

  const ratingByDriverOptions = {
    chart: { ...BASE_CHART, type: 'bar' },
    colors: ratingBarColors,
    plotOptions: {
      bar: {
        horizontal: true,
        // Per-bar colour, so each is coloured by its own side of the line.
        distributed: true,
        barHeight: '58%',
        borderRadius: 4,
        borderRadiusApplication: 'end',
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (value, { dataPointIndex }) => {
        const rated = chartedRatedDrivers[dataPointIndex]?.rated || 0;
        return `${Number(value).toFixed(1)} · ${rated} rated`;
      },
      offsetX: -6,
      style: { fontSize: '11px', colors: ['#ffffff'], fontWeight: 600 },
    },
    annotations: {
      xaxis:
        fleetAverage != null
          ? [
              {
                x: fleetAverage,
                strokeDashArray: 4,
                borderColor: INK.secondary,
                label: {
                  text: `Average ${fleetAverage.toFixed(1)}`,
                  position: 'top',
                  orientation: 'horizontal',
                  offsetY: -4,
                  borderColor: 'transparent',
                  style: {
                    background: INK.surface,
                    color: INK.secondary,
                    fontSize: '11px',
                    fontWeight: 600,
                  },
                },
              },
            ]
          : [],
    },
    grid: { ...BASE_GRID, yaxis: { lines: { show: false } }, padding: { ...BASE_GRID.padding, top: 8 } },
    xaxis: {
      categories: chartedRatedDrivers.map(driverLabel),
      min: 0,
      max: 5,
      tickAmount: 5,
      labels: { ...AXIS_LABEL, formatter: (value) => Number(value).toFixed(0) },
      axisBorder: { color: INK.grid },
      axisTicks: { color: INK.grid },
    },
    // Full driver names - this is the point of the horizontal form.
    yaxis: { labels: { ...AXIS_LABEL, maxWidth: 220 } },
    legend: { show: false },
    tooltip: {
      y: {
        formatter: (value, { dataPointIndex }) => {
          const driver = chartedRatedDrivers[dataPointIndex];
          const side =
            fleetAverage == null
              ? ''
              : driver.avgRating >= fleetAverage
              ? ' · at or above average'
              : ' · below average';
          return `${Number(value).toFixed(1)} / 5 from ${driver?.rated || 0} rated · ${driver?.deliveries || 0} deliveries${side}`;
        },
      },
    },
  };

  const ratingByDriverSeries = [
    {
      name: 'Average rating',
      data: chartedRatedDrivers.map((d) => Number(d.avgRating?.toFixed(2))),
    },
  ];

  /* Dispatch location share of deliveries, as a pie.
     Part-to-whole at a glance only - the tail past the top five folds into one
     "Other" slice rather than slicing the circle into unreadable slivers or
     generating hues past the fixed eight. Exact numbers live in the tooltip
     and in the table below, which a pie cannot give you. */
  const locations = summary?.dispatchLocations || [];
  const pieSlices = (() => {
    if (locations.length <= MAX_PIE_SLICES) {
      return locations.map((l) => ({ ...l, isOther: false }));
    }
    const head = locations.slice(0, MAX_PIE_SLICES - 1);
    const tail = locations.slice(MAX_PIE_SLICES - 1);
    const folded = tail.reduce(
      (sum, l) => ({
        dispatchLocation: `Other (${tail.length} locations)`,
        deliveries: sum.deliveries + l.deliveries,
        success: sum.success + l.success,
        partial: sum.partial + l.partial,
        failed: sum.failed + l.failed,
        rated: sum.rated + l.rated,
        avgRating: null,
        isOther: true,
      }),
      { deliveries: 0, success: 0, partial: 0, failed: 0, rated: 0 },
    );
    return [...head.map((l) => ({ ...l, isOther: false })), folded];
  })();

  const locationOptions = {
    chart: { ...BASE_CHART, type: 'pie' },
    // "Other" is not an entity, so it wears the de-emphasis grey rather than a
    // categorical slot.
    colors: pieSlices.map((slice, index) =>
      slice.isOther ? OTHER_GREY : CATEGORICAL[index % CATEGORICAL.length],
    ),
    labels: pieSlices.map((slice) => slice.dispatchLocation),
    // 2px of surface between slices so neighbours stay separate.
    stroke: { width: 2, colors: [INK.surface] },
    dataLabels: {
      enabled: true,
      // Share only - the count would not fit on a thin slice.
      formatter: (percent) => `${Number(percent).toFixed(0)}%`,
      style: {
        fontSize: '12px',
        fontWeight: 600,
        colors: pieSlices.map((slice, index) =>
          slice.isOther
            ? SLICE_LABEL_INK[SLICE_LABEL_INK.length - 1]
            : SLICE_LABEL_INK[index % CATEGORICAL.length],
        ),
      },
      dropShadow: { enabled: false },
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '12px',
      labels: { colors: INK.secondary },
      markers: { radius: 3 },
      itemMargin: { horizontal: 8, vertical: 2 },
    },
    tooltip: {
      y: {
        formatter: (value, { seriesIndex }) => {
          const slice = pieSlices[seriesIndex];
          if (!slice) return `${value} deliveries`;
          const rating =
            slice.avgRating != null
              ? ` · ${slice.avgRating.toFixed(1)}/5 from ${slice.rated} rated`
              : '';
          return `${value} deliveries — ${slice.success} delivered, ${slice.partial} partial, ${slice.failed} failed${rating}`;
        },
      },
    },
  };

  const locationSeries = pieSlices.map((slice) => slice.deliveries);

  /* Overall score is one ratio against a limit (x out of 5), which is a meter -
     a radial track filled to the score, with the figure in the middle. */
  const gaugeOptions = {
    chart: { ...BASE_CHART, type: 'radialBar', sparkline: { enabled: true } },
    colors: [SERIES_BLUE],
    plotOptions: {
      radialBar: {
        hollow: { size: '58%' },
        // Same-ramp track: the 100 step of the chart's own blue, so the unfilled
        // remainder reads as "the limit" and the fill still clears 3:1 on it.
        track: { background: '#cde2fb', strokeWidth: '100%' },
        dataLabels: {
          name: {
            offsetY: 16,
            fontSize: '11px',
            color: INK.muted,
            formatter: () => 'out of 5',
          },
          value: {
            offsetY: -9,
            fontSize: '20px',
            fontWeight: 700,
            color: INK.primary,
            // The track is a percentage; the label shows the real score.
            formatter: () =>
              totals?.avgRating != null ? totals.avgRating.toFixed(1) : '–',
          },
        },
      },
    },
    stroke: { lineCap: 'round' },
    labels: ['out of 5'],
  };

  const gaugeSeries = [
    totals?.avgRating != null ? Math.round((totals.avgRating / 5) * 100) : 0,
  ];

  /* Rating mix. Ordered buckets, so colour carries the order. */
  const distributionOptions = {
    chart: { ...BASE_CHART, type: 'bar' },
    colors: RATING_RAMP,
    plotOptions: {
      bar: {
        distributed: true,
        columnWidth: '52%',
        borderRadius: 4,
        borderRadiusApplication: 'end',
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: { fontSize: '11px', colors: [INK.secondary] },
    },
    legend: { show: false },
    grid: { ...BASE_GRID, padding: { ...BASE_GRID.padding, top: 16 } },
    xaxis: {
      categories: distribution.map((bucket) => `${bucket.rating}★`),
      labels: AXIS_LABEL,
      axisBorder: { color: INK.grid },
      axisTicks: { color: INK.grid },
    },
    yaxis: { labels: AXIS_LABEL },
    tooltip: { y: { title: { formatter: () => 'Reviews' } } },
  };

  const distributionSeries = [
    { name: 'Reviews', data: distribution.map((bucket) => bucket.count) },
  ];

  // Full plot height - one row per driver, so bars keep a readable thickness
  // however many there are. ScrollablePlot caps what is on screen.
  const outcomeHeight = Math.max(200, chartedDrivers.length * 26 + 60);
  // Columns, so height is fixed; the x axis carries the driver count.
  // Capped list, so the height is bounded whatever the driver count.
  const ratingHeight = Math.max(200, chartedRatedDrivers.length * 30 + 70);
  // Columns, so height is fixed - width carries the category count instead.
  const locationHeight = 236;

  /* Leaders. The charts cap at the busiest / highest rated few, so these name
     the standouts across every driver, not just the charted slice. */
  const busiestDriver = drivers[0] || null;

  // Preferred pool is drivers with enough ratings to be comparable; if nobody
  // clears the bar, fall back to anyone rated rather than show nothing.
  const comparable = ratedDrivers.filter((d) => d.rated >= MIN_RATINGS_FOR_BEST);
  const ratingPool = comparable.length > 0 ? comparable : ratedDrivers;
  const bestRatedDriver = ratingPool.reduce(
    // Ties go to the driver with more ratings behind the score.
    (best, d) =>
      !best ||
      d.avgRating > best.avgRating ||
      (d.avgRating === best.avgRating && d.rated > best.rated)
        ? d
        : best,
    null,
  );

  const mostFailedDriver = drivers.reduce(
    (worst, d) => (!worst || d.failed > worst.failed ? d : worst),
    null,
  );
  const hasFailures = mostFailedDriver && mostFailedDriver.failed > 0;

  return (
    <Row className="mb-3">
      <Col xl={12}>
        <Card className="mb-3">
          <CardBody>
            {isLoading && !summary ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : !hasData ? (
              <p className="mb-0 text-center py-3" style={{ color: INK.muted }}>
                No deliveries captured for these filters.
              </p>
            ) : (
              <>
                <Row className="g-2">
                  <StatTile label="Deliveries" value={totals.deliveries} />
                  <StatTile label="Drivers" value={totals.drivers} />
                  <StatTile
                    label="Rated"
                    value={totals.rated}
                    hint={`${Math.round((totals.rated / totals.deliveries) * 100)}% of deliveries`}
                  />
                  {/* A score out of 5 is a ratio against a limit, so it reads
                      as a meter rather than a bare number. */}
                  <Col xs={6} md={3}>
                    <div
                      className="px-3 h-100 border rounded d-flex align-items-center justify-content-between"
                      style={{ minHeight: TILE_HEIGHT }}
                    >
                      <div>
                        <div
                          className="text-uppercase"
                          style={{ fontSize: '11px', letterSpacing: '0.4px', color: INK.muted }}
                        >
                          Avg Rating
                        </div>
                        <div style={{ fontSize: '11px', color: INK.secondary }}>
                          {totals.avgRating != null
                            ? `${totals.rated} ratings`
                            : 'no ratings yet'}
                        </div>
                      </div>
                      <ReactApexChart
                        options={gaugeOptions}
                        series={gaugeSeries}
                        type="radialBar"
                        height={90}
                        width={90}
                      />
                    </div>
                  </Col>
                </Row>

                <Row className="mt-3 g-2">
                  {busiestDriver ? (
                    <HighlightTile
                      label="Most deliveries"
                      name={driverLabel(busiestDriver)}
                      value={`${busiestDriver.deliveries} deliveries`}
                      hint={`${busiestDriver.success} delivered · ${busiestDriver.partial} partial · ${busiestDriver.failed} failed`}
                    />
                  ) : null}
                  {bestRatedDriver ? (
                    <HighlightTile
                      label="Highest rated"
                      name={driverLabel(bestRatedDriver)}
                      value={`${bestRatedDriver.avgRating.toFixed(1)} / 5`}
                      hint={
                        comparable.length > 0
                          ? `from ${bestRatedDriver.rated} ratings · ${bestRatedDriver.deliveries} deliveries`
                          : `from ${bestRatedDriver.rated} rating${bestRatedDriver.rated === 1 ? '' : 's'} — too few to compare`
                      }
                      accent={DIVERGING.above}
                    />
                  ) : null}
                  {hasFailures ? (
                    <HighlightTile
                      label="Most failed deliveries"
                      name={driverLabel(mostFailedDriver)}
                      value={`${mostFailedDriver.failed} failed`}
                      hint={`of ${mostFailedDriver.deliveries} deliveries`}
                      accent={STATUS.failed}
                    />
                  ) : null}
                </Row>

                {/* Row 2 - per driver volume, then score, then score spread. */}
                {/* Scores get the full width - longest list, and the
                    baseline comparison needs the room. */}
                <Row className="mt-3 g-3">
                  {ratedDrivers.length > 0 ? (
                    <Col xl={12}>
                      <ChartCard
                        title="Average rating by driver"
                        subtitle={`Highest rated first · ${
                          ratedDrivers.length > MAX_DRIVER_BARS
                            ? `top ${MAX_DRIVER_BARS} of ${ratedDrivers.length} rated drivers`
                            : `${ratedDrivers.length} of ${drivers.length} drivers rated`
                        } · fleet average ${
                          fleetAverage != null ? fleetAverage.toFixed(1) : '–'
                        }`}
                      >
                        <ColourKey
                          items={[
                            { label: 'At or above average', color: DIVERGING.above },
                            { label: 'Below average', color: DIVERGING.below },
                          ]}
                        />
                        <ReactApexChart
                          options={ratingByDriverOptions}
                          series={ratingByDriverSeries}
                          type="bar"
                          height={ratingHeight}
                        />
                      </ChartCard>
                    </Col>
                  ) : null}
                </Row>

                {/* One row of thirds: where deliveries came from, who made
                    them, and how the scores landed. */}
                <Row className="mt-3 g-3">
                  {locations.length > 0 ? (
                    <Col md={4}>
                      <ChartCard
                        title="Deliveries by dispatch location"
                        subtitle={`Share of deliveries · ${locations.length} locations${
                          locations.length > MAX_PIE_SLICES
                            ? `, smallest ${locations.length - (MAX_PIE_SLICES - 1)} grouped as Other`
                            : ''
                        }`}
                      >
                        <ReactApexChart
                          options={locationOptions}
                          series={locationSeries}
                          type="pie"
                          height={locationHeight}
                        />
                      </ChartCard>
                    </Col>
                  ) : null}
                  <Col md={4}>
                    <ChartCard
                      title="Deliveries by driver"
                      subtitle={`Split by delivery outcome · ${
                        drivers.length > MAX_DRIVER_BARS
                          ? `busiest ${MAX_DRIVER_BARS} of ${drivers.length} drivers, rest grouped as Other`
                          : `${drivers.length} drivers`
                      }`}
                    >
                      <ScrollablePlot height={outcomeHeight}>
                        <ReactApexChart
                          options={outcomeOptions}
                          series={outcomeSeries}
                          type="bar"
                          height={outcomeHeight}
                        />
                      </ScrollablePlot>
                    </ChartCard>
                  </Col>
                  <Col md={4}>
                    <ChartCard
                      title="Rating mix"
                      subtitle="How many reviews gave each score"
                    >
                      <ReactApexChart
                        options={distributionOptions}
                        series={distributionSeries}
                        type="bar"
                        height={236}
                      />
                    </ChartCard>
                  </Col>
                </Row>

              </>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default DriverAnalysisSummary;
